import type {
    MemberRoom,
    MembersRecent,
    MemberWebcam,
    RecentMemberRoom,
    RecentMemberWebcam,
} from '../../types'
import { getLocalStorageValue, setLocalStorage } from '../utilities/storage'
import { getSessionKey } from './history'

export const MEMBERS_RECENT_MAX = 10

const MEMBERS_RECENT_PREFIX = 'MEMBERS_RECENT_'
const MEMBERS_SEARCH_PREFIX = 'MEMBERS_SEARCH_'

const isRecentRoom = (value: unknown): value is RecentMemberRoom => {
    const item = value as Partial<RecentMemberRoom> | null
    return (
        !!item &&
        typeof item.ownerId === 'number' &&
        typeof item.ownerName === 'string' &&
        typeof item.name === 'string' &&
        typeof item.at === 'number'
    )
}

const isRecentWebcam = (value: unknown): value is RecentMemberWebcam => {
    const item = value as Partial<RecentMemberWebcam> | null
    return (
        !!item &&
        typeof item.id === 'number' &&
        typeof item.name === 'string' &&
        typeof item.at === 'number'
    )
}

export const emptyMembersRecent = (): MembersRecent => ({
    rooms: [],
    webcams: [],
})

export const readMembersRecent = (): MembersRecent => {
    if (typeof window === 'undefined') return emptyMembersRecent()
    try {
        const stored = JSON.parse(
            getLocalStorageValue(getSessionKey(MEMBERS_RECENT_PREFIX)) ??
                'null',
        )
        return {
            rooms: Array.isArray(stored?.rooms)
                ? stored.rooms.filter(isRecentRoom).slice(0, MEMBERS_RECENT_MAX)
                : [],
            webcams: Array.isArray(stored?.webcams)
                ? stored.webcams
                      .filter(isRecentWebcam)
                      .slice(0, MEMBERS_RECENT_MAX)
                : [],
        }
    } catch {
        return emptyMembersRecent()
    }
}

export const writeMembersRecent = (recent: MembersRecent) => {
    if (typeof window === 'undefined') return
    setLocalStorage(
        getSessionKey(MEMBERS_RECENT_PREFIX),
        JSON.stringify(recent),
    )
}

export const readMembersSearchHistory = (): Array<string> => {
    if (typeof window === 'undefined') return []
    try {
        const stored = JSON.parse(
            getLocalStorageValue(getSessionKey(MEMBERS_SEARCH_PREFIX)) ?? '[]',
        )
        return Array.isArray(stored)
            ? stored.filter((item) => typeof item === 'string' && !!item)
            : []
    } catch {
        return []
    }
}

export const writeMembersSearchHistory = (history: Array<string>) => {
    if (typeof window === 'undefined') return
    setLocalStorage(
        getSessionKey(MEMBERS_SEARCH_PREFIX),
        JSON.stringify(history),
    )
}

export const addRecentMemberRoom = (
    recent: MembersRecent,
    room: Pick<MemberRoom, 'ownerId' | 'ownerName' | 'name'>,
    at = Date.now(),
): MembersRecent => ({
    ...recent,
    rooms: [
        {
            ownerId: room.ownerId,
            ownerName: room.ownerName,
            name: room.name,
            at,
        },
        ...recent.rooms.filter((item) => item.ownerId !== room.ownerId),
    ].slice(0, MEMBERS_RECENT_MAX),
})

export const removeRecentMemberRoom = (
    recent: MembersRecent,
    ownerId: number,
): MembersRecent => ({
    ...recent,
    rooms: recent.rooms.filter((item) => item.ownerId !== ownerId),
})

export const addRecentMemberWebcam = (
    recent: MembersRecent,
    member: Pick<MemberWebcam, 'id' | 'name'>,
    at = Date.now(),
): MembersRecent => ({
    ...recent,
    webcams: [
        { id: member.id, name: member.name, at },
        ...recent.webcams.filter((item) => item.id !== member.id),
    ].slice(0, MEMBERS_RECENT_MAX),
})
